import { useState, useEffect, useRef } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { ProjectAvatar } from "@/workshop/components/panels/project-avatar";

/**
 * Workshop: SearchModal — Command palette / search overlay.
 *
 * Triggered by ⌘K. Scoped to current project or all projects.
 * Supports grouped results, keyboard navigation, and match highlighting.
 */

export interface SearchResult {
  id: string;
  type:
    | "project"
    | "space"
    | "frame"
    | "agent"
    | "memory-node"
    | "memory-edge"
    | "memory-summary";
  name: string;
  breadcrumb: string;
  icon?: React.ReactNode;
  color?: string;
}

export interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  projectName?: string;
  results?: Record<string, SearchResult[]>;
  recentItems?: SearchResult[];
  onSelect?: (result: SearchResult) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getTypeIcon(result: SearchResult): React.ReactNode {
  if (result.type === "project" && result.color) {
    return (
      <ProjectAvatar
        name={result.name}
        color={result.color}
        size="sm"
        data-testid="search-result-project-avatar"
      />
    );
  }
  if (result.icon) return result.icon;

  switch (result.type) {
    case "project":
      return <Icons.Brain className="h-3.5 w-3.5 text-muted" />;
    case "space":
      return <Icons.Layers className="h-3.5 w-3.5 text-muted" />;
    case "frame":
      return <Icons.FileText className="h-3.5 w-3.5 text-muted" />;
    case "agent":
      return <Icons.Bot className="h-3.5 w-3.5 text-muted" />;
    case "memory-node":
    case "memory-edge":
    case "memory-summary":
      return <Icons.Brain className="h-3.5 w-3.5 text-muted" />;
    default:
      return <Icons.FileText className="h-3.5 w-3.5 text-muted" />;
  }
}

/**
 * Wraps the matched portion of `text` in a <mark> element.
 * Returns an array of string | JSX elements.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerText.indexOf(lowerQuery);

  if (idx === -1) return text;

  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + query.length);
  const after = text.slice(idx + query.length);

  return (
    <>
      {before}
      <mark className="bg-accent/30 text-primary rounded-[2px] not-italic">
        {match}
      </mark>
      {after}
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ResultRowProps {
  result: SearchResult;
  isSelected: boolean;
  query: string;
  onClick: () => void;
  onMouseEnter: () => void;
  testId: string;
}

function ResultRow({
  result,
  isSelected,
  query,
  onClick,
  onMouseEnter,
  testId,
}: ResultRowProps) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors",
        isSelected
          ? "bg-accent/15 text-primary"
          : "text-primary hover:bg-[var(--color-glass-fill-heavy)]",
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      data-testid={testId}
    >
      {/* Type icon */}
      <span className="shrink-0">{getTypeIcon(result)}</span>

      {/* Name */}
      <span className="min-w-0 flex-1 truncate text-sm font-medium leading-none">
        {highlightMatch(result.name, query)}
      </span>

      {/* Breadcrumb */}
      <span className="shrink-0 truncate text-xs text-muted">
        {result.breadcrumb}
      </span>
    </button>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function SearchModal({
  open,
  onClose,
  projectName = "Current Project",
  results,
  recentItems,
  onSelect,
}: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeScope, setActiveScope] = useState<"project" | "all">("project");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      // Auto-focus input after mount
      const raf = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  // Build flat list of all results for keyboard nav
  const allResults: SearchResult[] = (() => {
    if (!query.trim()) {
      return recentItems ?? [];
    }
    if (!results) return [];
    return Object.values(results).flat();
  })();

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          allResults.length === 0 ? 0 : (prev + 1) % allResults.length,
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          allResults.length === 0
            ? 0
            : (prev - 1 + allResults.length) % allResults.length,
        );
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const selected = allResults[selectedIndex];
        if (selected) {
          onSelect?.(selected);
          onClose();
        }
        return;
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, allResults, selectedIndex, onClose, onSelect]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, activeScope]);

  if (!open) return null;

  const truncatedProjectName =
    projectName.length > 20 ? projectName.slice(0, 20) + "…" : projectName;

  const hasQuery = query.trim().length > 0;

  // Determine display sections
  let sections: Array<{ label: string; items: SearchResult[] }> = [];

  if (!hasQuery) {
    if (recentItems && recentItems.length > 0) {
      sections = [{ label: "Recent", items: recentItems }];
    }
  } else if (results) {
    sections = Object.entries(results)
      .filter(([, items]) => items.length > 0)
      .map(([label, items]) => ({ label, items }));
  }

  const hasResults = sections.some((s) => s.items.length > 0);

  // Map each result to its flat index for selection tracking
  let flatIndex = 0;
  const indexedSections = sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({ item, index: flatIndex++ })),
  }));

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="search-modal-backdrop"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className={cn(
          "fixed left-1/2 top-[20%] z-50 w-full max-w-lg -translate-x-1/2",
          "rounded-xl border border-[var(--color-glass-stroke)]",
          "bg-[var(--color-glass-fill-dense)] shadow-[var(--shadow-glass)]",
          "backdrop-blur-xl",
        )}
        data-testid="search-modal"
      >
        {/* Search input bar */}
        <div className="flex items-center gap-3 border-b border-[var(--color-glass-stroke)] px-4 py-3">
          <Icons.Search className="h-4 w-4 shrink-0 text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className={cn(
              "min-w-0 flex-1 bg-transparent text-sm text-primary outline-none",
              "placeholder:text-muted",
            )}
            data-testid="search-modal-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <kbd className="shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-glass-fill-heavy)] px-1.5 py-0.5 font-mono text-[10px] text-muted">
            ⌘K
          </kbd>
        </div>

        {/* Scope tabs */}
        <div
          className="flex items-center gap-0 border-b border-[var(--color-glass-stroke)] px-4"
          role="tablist"
          aria-label="Search scope"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeScope === "project"}
            className={cn(
              "relative py-2 pr-4 text-xs font-medium transition-colors",
              activeScope === "project"
                ? "text-primary"
                : "text-muted hover:text-secondary",
            )}
            onClick={() => setActiveScope("project")}
            data-testid="search-scope-tab-project"
          >
            {truncatedProjectName}
            {activeScope === "project" && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full bg-accent" />
            )}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeScope === "all"}
            className={cn(
              "relative py-2 pr-4 text-xs font-medium transition-colors",
              activeScope === "all"
                ? "text-primary"
                : "text-muted hover:text-secondary",
            )}
            onClick={() => setActiveScope("all")}
            data-testid="search-scope-tab-all"
          >
            All projects
            {activeScope === "all" && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full bg-accent" />
            )}
          </button>
        </div>

        {/* Results area */}
        <div
          className="max-h-[360px] overflow-y-auto px-2 py-2"
          data-testid="search-results-area"
        >
          {/* Placeholder when no query and no recent items */}
          {!hasQuery && !hasResults && (
            <p className="px-3 py-8 text-center text-xs text-muted">
              Type to search projects, spaces, frames…
            </p>
          )}

          {/* No results for query */}
          {hasQuery && !hasResults && (
            <p className="px-3 py-8 text-center text-xs text-muted">
              No results for &ldquo;{query}&rdquo;
            </p>
          )}

          {/* Grouped result sections */}
          {indexedSections.map((section) => (
            <div key={section.label} className="mb-2 last:mb-0">
              {/* Section header */}
              <p className="mb-1 px-3 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
                {section.label}
              </p>

              {section.items.map(({ item, index }) => (
                <ResultRow
                  key={item.id}
                  result={item}
                  isSelected={index === selectedIndex}
                  query={hasQuery ? query : ""}
                  onClick={() => {
                    onSelect?.(item);
                    onClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  testId={`search-result-${item.id}`}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Footer — keyboard hints */}
        <div className="flex items-center gap-4 border-t border-[var(--color-glass-stroke)] px-4 py-2">
          <span className="text-[10px] text-muted">
            <kbd className="font-mono">↑↓</kbd> navigate
          </span>
          <span className="text-[10px] text-muted">
            <kbd className="font-mono">↵</kbd> open
          </span>
          <span className="text-[10px] text-muted">
            <kbd className="font-mono">esc</kbd> close
          </span>
        </div>
      </div>
    </>
  );
}
